import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SiteFooter from '../../components/SiteFooter';

/**
 * 법적 고지 문서(개인정보처리방침, 이용약관 등) 공통 프리미티브.
 * 각 페이지는 자체 헤더·푸터를 갖되 본문 요소는 이 파일을 공유한다.
 */

export const ROOT_FONT_STYLE = {
  fontFamily: '"Pretendard", -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Malgun Gothic", sans-serif',
};

export const LOGO_FONT_STYLE = {
  fontFamily: '"Avenir Next", -apple-system, BlinkMacSystemFont, sans-serif',
  fontWeight: 900,
};

/**
 * 초안 단계에서 채워야 할 값 표시.
 * 정식 게시 전 이 컴포넌트 사용처가 0건이어야 한다.
 */
export function Todo({ children }: { children?: ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded border border-dashed border-amber-700/70 bg-amber-950/60 px-1.5 py-0.5 font-bold text-amber-300">
      【확인 필요{children ? <>: {children}</> : null}】
    </span>
  );
}

/**
 * 번호가 붙는 조항. no를 생략하면 제목만 표시한다(부칙 등).
 * h2의 id는 목차(TOC) 앵커로 쓰인다 — 목차는 마운트 후 DOM에서 h2[id^="sec-"]를
 * 수집하므로, 조항을 추가·삭제해도 페이지 쪽 코드는 손댈 필요가 없다.
 */
export function Section({ no, title, children }: { no?: number; title: string; children: ReactNode }) {
  const id = no !== undefined ? `sec-${no}` : `sec-${title}`;
  const tocLabel = no !== undefined ? `${no}. ${title}` : title;
  return (
    <section className="mt-12 border-t border-gray-800/80 pt-8">
      <h2 id={id} data-toc-label={tocLabel} className="mb-5 scroll-mt-8 text-xl font-bold leading-snug text-white md:text-[22px]">
        {no !== undefined && <span className="mr-2 font-black text-red-500">{no}.</span>}
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Sub({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-3 mt-8 border-l-2 border-red-500/70 pl-3 text-base font-bold leading-snug text-gray-100">
      {children}
    </h3>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="mb-4 leading-[1.85]">{children}</p>;
}

/**
 * 표 — 풀 그리드 대신 수평선 위주 + 줄무늬로 시선 피로를 줄인다.
 * 셀 간 세로 구분은 first-child 제외 좌측 보더로만 넣는다(2열 "구분/내용" 표 대응).
 */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 overflow-hidden rounded-lg ring-1 ring-gray-800">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse text-sm [&>tbody>tr:nth-child(even)]:bg-gray-800/25">
          {children}
        </table>
      </div>
    </div>
  );
}

export function Th({ children, colSpan }: { children: ReactNode; colSpan?: number }) {
  return (
    <th
      colSpan={colSpan}
      className="whitespace-nowrap border-b border-gray-700 border-l border-l-gray-800/60 bg-gray-800/70 px-3.5 py-3 text-left font-semibold text-gray-100 first:border-l-0"
    >
      {children}
    </th>
  );
}

export function Td({ children, rowSpan }: { children: ReactNode; rowSpan?: number }) {
  return (
    <td
      rowSpan={rowSpan}
      className="border-b border-gray-800/70 border-l border-l-gray-800/60 px-3.5 py-3 align-top leading-relaxed first:border-l-0 [tr:last-child>&]:border-b-0"
    >
      {children}
    </td>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 rounded-lg border border-gray-800 border-l-[3px] border-l-red-500 bg-gray-800/40 px-4 py-3.5">
      <p className="leading-[1.85]">{children}</p>
    </div>
  );
}

export function Ul({ children }: { children: ReactNode }) {
  return <ul className="mb-4 list-disc space-y-1.5 pl-6 leading-[1.85] marker:text-red-500/70">{children}</ul>;
}

export function Ol({ children }: { children: ReactNode }) {
  return <ol className="mb-4 list-decimal space-y-1.5 pl-6 leading-[1.85] marker:text-gray-500">{children}</ol>;
}

export function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-white">{children}</strong>;
}

/** 법령 근거 표기 — 「법령명」 제O조 형식 */
export function Basis({ children }: { children: ReactNode }) {
  return (
    <p className="mb-4 flex gap-1.5 text-[13px] leading-relaxed text-gray-500">
      <span className="shrink-0 font-semibold text-gray-400">관련근거</span>
      <span>{children}</span>
    </p>
  );
}

/** 마운트 후 본문에서 조항(h2[id^="sec-"])을 수집한다. 본문은 정적이므로 1회면 충분하다. */
function useTocSections() {
  const [sections, setSections] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => {
    const headings = document.querySelectorAll<HTMLHeadingElement>('article h2[id^="sec-"]');
    setSections(Array.from(headings).map((h) => ({ id: h.id, label: h.dataset.tocLabel ?? h.textContent ?? '' })));
  }, []);
  return sections;
}

/** 화면에 보이는 조항을 추적해 목차 하이라이트에 쓴다. */
function useActiveSection(sections: { id: string }[]) {
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (sections.length === 0) return;
    setActiveId(sections[0].id);

    // 교차 중인 조항 집합을 유지하고, 문서 순서상 가장 앞의 것을 활성으로 삼는다.
    // 마지막 이벤트를 그대로 쓰면 초기 레이아웃 시프트 때 뒤쪽 조항이 이겨버린다.
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const first = sections.find((s) => visible.has(s.id));
        if (first) setActiveId(first.id);
      },
      // 화면 상단 0~25% 밴드를 지나는 조항을 활성으로 본다.
      // 밴드 하한을 0으로 두어야 앵커 점프 직후(헤딩이 최상단 부근) 활성이 잡힌다.
      { rootMargin: '0px 0px -75% 0px' },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);
  return activeId;
}

/** 읽기 진행률(0~1)과 "맨 위로" 버튼 노출 여부. */
function useReadingProgress() {
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(window.scrollY / max, 1) : 0);
      setShowTop(window.scrollY > 600);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return { progress, showTop };
}

function scrollToSection(id: string) {
  // scroll-mt(h2)에 오프셋을 맡기고 부드럽게 이동. 해시도 남겨 새로고침·공유에 대응한다.
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  history.replaceState(null, '', `#${id}`);
}

function TocList({
  sections,
  activeId,
  onNavigate,
}: {
  sections: { id: string; label: string }[];
  activeId: string | null;
  onNavigate?: () => void;
}) {
  return (
    <ul className="space-y-0.5 text-[13px]">
      {sections.map((s) => {
        const active = s.id === activeId;
        return (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(s.id);
                onNavigate?.();
              }}
              className={`block break-keep border-l-2 py-1 pl-3 leading-snug transition-colors ${
                active
                  ? 'border-red-500 font-semibold text-white'
                  : 'border-transparent text-gray-500 hover:border-gray-700 hover:text-gray-300'
              }`}
            >
              {s.label}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 법적 고지 문서 공통 레이아웃.
 *
 * 구성 — 읽기 진행 바(상단 고정) / 헤더(로고·문서 전환 탭·제목) / 목차(xl 이상은
 * 우측 고정 사이드바, 미만은 접이식 블록) / 본문 / "맨 위로" 버튼 / 공통 푸터.
 *
 * 여백 규칙 — 모든 법적 문서가 동일한 상하 여백을 갖도록 한 곳에서 관리한다.
 * · 본문 영역 상단/하단: 컨테이너 py-14 (대칭)
 * · 본문 마지막 요소의 mb를 제거한다. 제거하지 않으면 하단만 벌어져 위아래가 어긋난다.
 * · 첫 조항의 mt/border-t/pt는 항상 제거한다. 헤더 구분선과 겹쳐 이중선이 되고,
 *   서문 유무에 따라 문서마다 첫 간격이 달라지기 때문이다. 서문은 children이
 *   아니라 intro로 받아 항상 첫 조항이 본문의 first-child가 되게 한다.
 *
 * 푸터는 홈과 동일한 SiteFooter를 쓴다. 문서마다 푸터를 따로 두면 사업자 정보가
 * 중복되어 어긋난다(실제로 주소가 홈에만 들어간 적이 있다).
 */
export function LegalPage({
  title,
  subtitle,
  intro,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  /** 서문. 본문 조항 앞에 오는 도입 문단 — 헤더 블록에 붙어 본문 리듬을 흐트리지 않는다. */
  intro?: ReactNode;
  children: ReactNode;
}) {
  const sections = useTocSections();
  const activeId = useActiveSection(sections);
  const { progress, showTop } = useReadingProgress();

  // 초기 스크롤 위치는 콘텐츠 마운트 후(조항 수집 완료 시점) 직접 잡는다.
  // - 해시 진입(#sec-N): 브라우저 네이티브 점프는 SPA에서 요소가 아직 없을 때 실행돼
  //   최상단에 떨어지는 경우가 있다 → 요소가 생긴 뒤 직접 이동.
  // - 해시 없음: SPA 라우팅은 스크롤을 유지하므로 문서 전환 탭으로 넘어오면 새 문서가
  //   중간부터 보인다 → 최상단에서 시작.
  useEffect(() => {
    if (sections.length === 0) return;
    const hashId = decodeURIComponent(location.hash.slice(1));
    const target = hashId ? document.getElementById(hashId) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [sections]);

  return (
    <div className="flex min-h-screen flex-col break-keep bg-gray-900 text-[15px] text-gray-300 md:text-base" style={ROOT_FONT_STYLE}>

      {/* 읽기 진행 바 */}
      <div className="fixed inset-x-0 top-0 z-50 h-0.5 bg-transparent">
        <div
          className="h-full bg-gradient-to-r from-red-400 to-red-600 transition-[width] duration-150 ease-out"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* flex-1: 본문이 짧은 페이지(공지 목록 등)에서도 푸터가 화면 하단에 붙도록 남는 높이를 채운다 */}
      <div className="mx-auto w-full max-w-6xl flex-1 px-5 py-14 xl:grid xl:grid-cols-[minmax(0,1fr)_240px] xl:gap-14">
        <div className="mx-auto w-full max-w-3xl xl:mx-0">

          <header className="border-b border-gray-800 pb-7">
            <Link to="/" className="mb-5 inline-block text-2xl font-black text-red-600 transition-colors hover:text-red-500" style={LOGO_FONT_STYLE}>
              Cupid
            </Link>

            {/* 시행일자는 제목 우측 정렬. 폭이 부족하면 flex-wrap으로 제목 아래로 내려간다. */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-4">
              <h1 className="text-3xl font-black leading-tight text-white md:text-4xl">{title}</h1>
              {subtitle && (
                <p className="ml-auto inline-block whitespace-nowrap rounded-full bg-gray-800/70 px-3.5 py-1.5 text-[13px] text-gray-400 ring-1 ring-inset ring-gray-700/60">
                  {subtitle}
                </p>
              )}
            </div>
          </header>

          {/* 접이식 목차 — 사이드바가 없는 화면 폭 전용 */}
          {sections.length > 0 && (
            <details className="mt-8 overflow-hidden rounded-lg border border-gray-800 bg-gray-800/30 xl:hidden">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-gray-200 transition-colors hover:text-white">
                목차
              </summary>
              <div className="border-t border-gray-800 px-4 py-3">
                <TocList sections={sections} activeId={activeId} />
              </div>
            </details>
          )}

          {intro && <div className="mt-8 [&>*:last-child]:mb-0">{intro}</div>}

          <article
            className="mt-2
              [&>section:first-child]:mt-10 [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0
              [&>section:last-child>*:last-child]:mb-0"
          >
            {children}
          </article>

        </div>

        {/* 데스크톱 목차 사이드바 */}
        {sections.length > 0 && (
          <aside className="hidden xl:block">
            <nav className="sticky top-12 max-h-[calc(100vh-6rem)] overflow-y-auto pb-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500">목차</p>
              <TocList sections={sections} activeId={activeId} />
            </nav>
          </aside>
        )}
      </div>

      {/* 맨 위로 */}
      <button
        type="button"
        aria-label="맨 위로"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-gray-800/90 text-gray-300 shadow-lg ring-1 ring-gray-700 backdrop-blur transition-all hover:bg-gray-700 hover:text-white ${
          showTop ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'
        }`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 15l7-7 7 7" />
        </svg>
      </button>

      <SiteFooter />
    </div>
  );
}

/**
 * 문서 제목 설정 및 색인 차단.
 *
 * SPA라 index.html의 <title>·<meta>가 라우트별로 갈리지 않으므로 마운트 시 주입한다.
 * noindex는 초안 단계 기본값이며, 정식 게시 시 호출부에서 false로 바꾼다.
 */
export function useLegalDocumentHead(title: string, noindex = true) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    let robots: HTMLMetaElement | null = null;
    if (noindex) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      robots.content = 'noindex, nofollow';
      document.head.appendChild(robots);
    }

    return () => {
      document.title = previousTitle;
      if (robots) document.head.removeChild(robots);
    };
  }, [title, noindex]);
}
